// import { v4 as uuidv4 } from 'uuid'; // Import uuid for unique group IDs
import { ProviderCohortsListResponse } from 'src/api/redux/providerCohorts';

// Define action types specific to Encounters
export const EncountersActionTypes: { [key: string]: string } = {
  SET_VIEW: 'SET_VIEW',
  SET_VOLUME_TYPE: 'SET_VOLUME_TYPE',
  SET_SELECTED_TYPE: 'SET_SELECTED_TYPE', // pertains to setting as 'personal' or 'company'
  SET_NEW_COHORT_NAME: 'SET_NEW_COHORT_NAME', // New action type for volume name input
  SET_SHOW_ADD_GROUP: 'SET_SHOW_ADD_GROUP',
  // New Billing Code Actions
  ADD_CODE_GROUP: 'ADD_CODE_GROUP',
  REMOVE_CODE_GROUP: 'REMOVE_CODE_GROUP', // Optional: If needed later
  ADD_CODE_TO_GROUP: 'ADD_CODE_TO_GROUP',
  REMOVE_CODE_FROM_GROUP: 'REMOVE_CODE_FROM_GROUP',
  SET_GROUP_OPERATOR: 'SET_GROUP_OPERATOR',
  SET_GROUP_INVERSE: 'SET_GROUP_INVERSE', // New action for INCLUDES/EXCLUDES
  SET_INTER_GROUP_OPERATOR: 'SET_INTER_GROUP_OPERATOR',
  SET_EDITING_GROUP_ID: 'SET_EDITING_GROUP_ID', // <-- New Action Type
  SET_EDITING_VOLUME_ID: 'SET_EDITING_VOLUME_ID', // New action type
  RESET_ENCOUNTERS_STATE: 'RESET_ENCOUNTERS_STATE',
  SET_EDITING_VOLUME_TYPE: 'SET_EDITING_VOLUME_TYPE', // New action
  SET_PROVIDER_COHORTS: 'SET_PROVIDER_COHORTS',
  SET_PROVIDER_COHORTS_LOADING: 'SET_PROVIDER_COHORTS_LOADING',
  SET_PROVIDER_COHORTS_ERROR: 'SET_PROVIDER_COHORTS_ERROR',
  SET_EDITING_LOCATION: 'SET_EDITING_LOCATION',
  SET_CODE_MODIFIER: 'SET_CODE_MODIFIER',
  REMOVE_CODE_MODIFIER: 'REMOVE_CODE_MODIFIER',
};

// Key for provider cohorts data
export const PROVIDER_COHORTS_DATA_KEY = 'providerCohortsData';
// Define volume type options
export enum VolumeType {
  TOTAL_CLAIMS = 'totalClaims',
  ENCOUNTERS = 'encounters',
}

export enum SavedType {
  PERSONAL = 'personal',
  COMPANY = 'company',
}

// Define Code Information Structure (Matches API suggestion structure)
export type CodeInfo = {
  id: string; // e.g., "CPT-81404"
  code?: string; // e.g., "81404"
  context_type?: string; // e.g., "cpt"
  description?: string;
  category?: string;
  modifier?: string; // e.g., "Q6"
  modifierDescription?: string; // human-readable description of the modifier
};

// Define Code Group Structure
export interface CodeGroupState {
  id: string; // Unique ID for the group
  codes: CodeInfo[];
  operator: 'ANY' | 'ALL'; // Operator within the group
  inverse: boolean; // Whether to INCLUDE (false) or EXCLUDE (true) codes
  modifier?: string; // Optional modifier for the group (e.g., "GP", "JT") if needed in the future
}

// Define Billing Codes State Structure
export interface BillingCodesState {
  groups: CodeGroupState[];
  anyAllOperator: 'AND' | 'OR'; // Operator between groups
}

// Define the shape of the Encounters state
export type EncountersState = {
  view: string;
  volumeType: VolumeType;
  selectedType: SavedType;
  newCohortName: string;
  showAddGroup: boolean;
  billingCodes: BillingCodesState; // Add billing codes state
  editingGroupId: string | null; // <-- New State Field
  editingVolumeId: string | null; // New field
  editingVolumeType: 'cohort' | 'filterSet' | null; // New field
  providerCohorts: ProviderCohortsListResponse | null;
  isLoadingProviderCohorts: boolean;
  providerCohortsError: Error | null;
  editingLocation?: string;
};

export enum EncountersView {
  VOLUME_BILLING = 'volumeBilling',
  NEW_CODE_SET = 'newCodeSet',
}

// Helper function to generate a simple unique ID
const generateSimpleId = (): string => {
  return `group-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

// Define the initial state for Encounters
export const initialEncountersState: EncountersState = {
  view: EncountersView.VOLUME_BILLING,
  volumeType: VolumeType.TOTAL_CLAIMS,
  selectedType: SavedType.PERSONAL,
  newCohortName: '',
  showAddGroup: false,
  // Initialize billingCodes with one default group
  billingCodes: {
    groups: [
      {
        id: generateSimpleId(), // Use the new ID generator
        codes: [],
        operator: 'ANY',
        inverse: false, // Default to INCLUDES
      },
    ],
    anyAllOperator: 'AND', // Default operator between groups
  },
  editingGroupId: null, // <-- Initialize new field
  editingVolumeId: null, // Initialize new field
  editingVolumeType: null, // Initialize new field
  providerCohorts: null,
  isLoadingProviderCohorts: false,
  providerCohortsError: null,
  editingLocation: undefined,
};

// Define the reducer function for Encounters
export const encountersReducer = (
  state = initialEncountersState,
  action: any
): EncountersState => {
  switch (action.type) {
    case EncountersActionTypes.SET_VIEW:
      if (action.payload) {
        return {
          ...state,
          view: action.payload.view,
        };
      }

      return state;

    case EncountersActionTypes.SET_VOLUME_TYPE:
      if (
        action.payload &&
        Object.values(VolumeType).includes(action.payload.volumeType)
      ) {
        const newVolumeType = action.payload.volumeType as VolumeType;
        const isTotalClaims = newVolumeType === VolumeType.TOTAL_CLAIMS;
        const showAddGroup = isTotalClaims ? false : state.showAddGroup;

        // Modifiers are only supported for Encounters. When switching to Total
        // Claims, strip modifiers from every code so an unsupported modifier
        // can't stick around. Stripping can collapse two entries that differed
        // only by modifier into the same code, so de-duplicate by id per group.
        const billingCodes = isTotalClaims
          ? {
              ...state.billingCodes,
              groups: state.billingCodes.groups.map((group) => {
                const seen = new Set<string>();
                return {
                  ...group,
                  codes: group.codes.reduce<CodeInfo[]>((acc, code) => {
                    if (seen.has(code.id)) return acc;
                    seen.add(code.id);
                    acc.push({
                      ...code,
                      modifier: undefined,
                      modifierDescription: undefined,
                    });
                    return acc;
                  }, []),
                };
              }),
            }
          : state.billingCodes;

        return {
          ...state,
          volumeType: newVolumeType,
          showAddGroup,
          billingCodes,
        };
      }
      return state;
    case EncountersActionTypes.SET_SHOW_ADD_GROUP:
      if (action.payload) {
        return {
          ...state,
          showAddGroup: action.payload.showAddGroup,
        };
      }
      return state;

    case EncountersActionTypes.SET_SELECTED_TYPE:
      if (
        action.payload &&
        Object.values(SavedType).includes(action.payload.selectedType)
      ) {
        return {
          ...state,
          selectedType: action.payload.selectedType as SavedType,
        };
      }
      return state;

    case EncountersActionTypes.SET_NEW_COHORT_NAME:
      if (action.payload) {
        return {
          ...state,
          newCohortName: action.payload.newCohortName,
        };
      }
      return state;

    // --- New Billing Code Reducer Logic ---

    case EncountersActionTypes.ADD_CODE_GROUP:
      // Only add if showAddGroup is true (or based on your logic)
      // Ensure we don't exceed a max number of groups if needed
      return {
        ...state,
        billingCodes: {
          ...state.billingCodes,
          groups: [
            ...state.billingCodes.groups,
            {
              id: generateSimpleId(), // Use the new ID generator
              codes: [],
              operator: 'ANY', // Default operator for new group
              inverse: false, // Default to INCLUDES
            },
          ],
        },
      };

    case EncountersActionTypes.ADD_CODE_TO_GROUP: {
      const { groupId, code } = action.payload;
      return {
        ...state,
        billingCodes: {
          ...state.billingCodes,
          groups: state.billingCodes.groups.map((group) =>
            group.id === groupId
              ? {
                  ...group,
                  // Add code only if it doesn't already exist in this group
                  // Check both id and modifier so the same code with different modifiers are distinct entries
                  codes: group.codes.some(
                    (c) => c.id === code.id && c.modifier === code.modifier
                  )
                    ? group.codes
                    : [...group.codes, code],
                }
              : group
          ),
        },
      };
    }

    case EncountersActionTypes.REMOVE_CODE_FROM_GROUP: {
      const { groupId, codeId } = action.payload;
      return {
        ...state,
        billingCodes: {
          ...state.billingCodes,
          groups: state.billingCodes.groups.map((group) =>
            group.id === groupId
              ? {
                  ...group,
                  codes: group.codes.filter((code) => code.id !== codeId),
                }
              : group
          ),
          // Optional: Remove group if it becomes empty after removing the code
          // groups: state.billingCodes.groups
          //   .map(group => ...) // map as above
          //   .filter(group => group.codes.length > 0 || state.billingCodes.groups.length === 1), // Keep at least one group
        },
      };
    }

    case EncountersActionTypes.SET_GROUP_OPERATOR: {
      const { groupId, operator } = action.payload;
      return {
        ...state,
        billingCodes: {
          ...state.billingCodes,
          groups: state.billingCodes.groups.map((group) =>
            group.id === groupId ? { ...group, operator } : group
          ),
        },
      };
    }

    case EncountersActionTypes.SET_GROUP_INVERSE: {
      const { groupId, inverse } = action.payload;
      return {
        ...state,
        billingCodes: {
          ...state.billingCodes,
          groups: state.billingCodes.groups.map((group) =>
            group.id === groupId ? { ...group, inverse } : group
          ),
        },
      };
    }

    case EncountersActionTypes.SET_INTER_GROUP_OPERATOR: {
      const { operator } = action.payload;
      return {
        ...state,
        billingCodes: {
          ...state.billingCodes,
          anyAllOperator: operator,
        },
      };
    }

    // Optional: REMOVE_CODE_GROUP logic if needed explicitly
    case EncountersActionTypes.REMOVE_CODE_GROUP: {
      const { groupId } = action.payload;
      // Prevent removing the last group
      if (state.billingCodes.groups.length <= 1) return state;
      return {
        ...state,
        billingCodes: {
          ...state.billingCodes,
          groups: state.billingCodes.groups.filter(
            (group) => group.id !== groupId
          ),
        },
      };
    }

    // --- New Editing Logic ---
    case EncountersActionTypes.SET_EDITING_GROUP_ID: // <-- New Reducer Case
      if (action.payload) {
        return {
          ...state,
          editingGroupId: action.payload.groupId, // Can be string or null
        };
      }
      return state;

    case EncountersActionTypes.SET_EDITING_VOLUME_ID:
      if (action.payload) {
        return {
          ...state,
          editingVolumeId: action.payload.volumeId,
        };
      }
      return state;

    case EncountersActionTypes.SET_EDITING_VOLUME_TYPE:
      if (action.payload) {
        return {
          ...state,
          editingVolumeType: action.payload.volumeType,
        };
      }
      return state;

    case EncountersActionTypes.RESET_ENCOUNTERS_STATE:
      return action.payload;

    case EncountersActionTypes.SET_PROVIDER_COHORTS:
      return {
        ...state,
        providerCohorts: action.payload,
        isLoadingProviderCohorts: false,
      };
    case EncountersActionTypes.SET_PROVIDER_COHORTS_LOADING:
      return {
        ...state,
        isLoadingProviderCohorts: action.payload,
      };
    case EncountersActionTypes.SET_PROVIDER_COHORTS_ERROR:
      return {
        ...state,
        providerCohortsError: action.payload,
        isLoadingProviderCohorts: false,
      };

    case EncountersActionTypes.SET_CODE_MODIFIER: {
      const { groupId, codeId, modifier, modifierDescription } = action.payload;
      return {
        ...state,
        billingCodes: {
          ...state.billingCodes,
          groups: state.billingCodes.groups.map((group) =>
            group.id === groupId
              ? {
                  ...group,
                  codes: group.codes.map((code) =>
                    code.id === codeId
                      ? { ...code, modifier, modifierDescription }
                      : code
                  ),
                }
              : group
          ),
        },
      };
    }

    case EncountersActionTypes.REMOVE_CODE_MODIFIER: {
      const { groupId, codeId, modifier } = action.payload;
      return {
        ...state,
        billingCodes: {
          ...state.billingCodes,
          groups: state.billingCodes.groups.map((group) =>
            group.id === groupId
              ? {
                  ...group,
                  codes: group.codes.map((code) =>
                    code.id === codeId && code.modifier === modifier
                      ? {
                          ...code,
                          modifier: undefined,
                          modifierDescription: undefined,
                        }
                      : code
                  ),
                }
              : group
          ),
        },
      };
    }

    case EncountersActionTypes.SET_EDITING_LOCATION: {
      return {
        ...state,
        editingLocation: action.payload.location,
      };
    }

    default:
      return state;
  }
};
